import React, { useState, useEffect, useCallback } from 'react';
import { emailIngestionAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import {
  Clock,
  Mail,
  FileCheck,
  RefreshCw,
} from 'lucide-react';

const METRICS_CONFIG = [
  {
    key: 'last_sync_at',
    label: 'Last Sync',
    icon: Clock,
    border: 'border-l-blue-500',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    isTime: true,
  },
  {
    key: 'emails_scanned_24h',
    label: 'Emails Scanned',
    icon: Mail,
    border: 'border-l-slate-500',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    sub: 'Last 24h',
  },
  {
    key: 'valid_rfqs_24h',
    label: 'Emails Staged',
    icon: FileCheck,
    border: 'border-l-emerald-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
    sub: 'For review',
  },
];

function formatRelativeTime(isoString) {
  if (!isoString) return 'Never';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  const hrs = Math.floor(diffMs / 3600000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function IngestionDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await emailIngestionAPI.getDashboardStats();
      setStats(res.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground" data-testid="ingestion-dashboard-loading">
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading ingestion stats...</span>
      </div>
    );
  }

  return (
    <div data-testid="ingestion-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
            Email Sync Status
          </h3>
        </div>
        <button
          onClick={() => { setLoading(true); fetchStats(); }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          data-testid="refresh-ingestion-stats"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {METRICS_CONFIG.map((m) => {
          const Icon = m.icon;
          const value = m.isTime
            ? formatRelativeTime(stats?.[m.key])
            : stats?.[m.key] ?? 0;

          return (
            <Card
              key={m.key}
              className={`border-l-4 ${m.border} hover:shadow-md transition-shadow`}
              data-testid={`ingestion-stat-${m.key}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide leading-tight">
                    {m.label}
                  </p>
                  <div className={`p-1.5 rounded-md ${m.bg}`}>
                    <Icon className={`h-3.5 w-3.5 ${m.text}`} />
                  </div>
                </div>
                <p className={`text-xl font-bold ${m.isTime ? 'text-foreground' : `font-mono ${m.text}`}`}>
                  {value}
                </p>
                {m.sub && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">{m.sub}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
